import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { reservationsApi, guestsApi, roomsApi, ratesApi, tcApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { format, parseISO } from 'date-fns';
import { 
  Plus, Search, CalendarDays, Users, LogIn, LogOut, Home, 
  Eye, Edit, X, CheckCircle, XCircle, Clock, DoorOpen, DoorClosed,
  User, Phone, Mail, CreditCard
} from 'lucide-react';

export default function ReservationsPage() {
  const { currentProperty } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [viewingReservation, setViewingReservation] = useState(null);
  const [cancelingReservation, setCancelingReservation] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    arrival_from: '',
    arrival_to: '',
    guest_name: '',
    confirmation: '',
  });

  const fetchReservations = useCallback(async () => {
    if (!currentProperty) return;
    try {
      setLoading(true);
      const params = { ...filters };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      
      const [resRes, statsRes] = await Promise.all([
        reservationsApi.list(currentProperty.id, params),
        reservationsApi.getStats(currentProperty.id)
      ]);
      setReservations(resRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load reservations');
    } finally {
      setLoading(false);
    }
  }, [currentProperty, filters]);

  const fetchTabData = useCallback(async () => {
    if (!currentProperty) return;
    try {
      setLoading(true);
      let response;
      switch (activeTab) {
        case 'arrivals':
          response = await reservationsApi.getArrivals(currentProperty.id);
          break;
        case 'departures':
          response = await reservationsApi.getDepartures(currentProperty.id);
          break;
        case 'inhouse':
          response = await reservationsApi.getInhouse(currentProperty.id);
          break;
        default:
          return fetchReservations();
      }
      setReservations(response.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [currentProperty, activeTab, fetchReservations]);

  useEffect(() => {
    if (activeTab === 'all') {
      fetchReservations();
    } else {
      fetchTabData();
    }
  }, [activeTab, fetchReservations, fetchTabData]);

  const handleCheckIn = async (reservation, roomId = null) => {
    try {
      await reservationsApi.checkIn(reservation.id, roomId);
      toast.success('Guest checked in successfully');
      fetchTabData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
  };

  const handleCheckOut = async (reservation) => {
    try {
      await reservationsApi.checkOut(reservation.id);
      toast.success('Guest checked out successfully');
      fetchTabData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-out failed');
    }
  };

  const handleCancel = async (reason) => {
    try {
      await reservationsApi.cancel(cancelingReservation.id, { reason });
      toast.success('Reservation cancelled');
      setCancelingReservation(null);
      fetchTabData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cancellation failed');
    }
  };

  const viewReservationDetails = async (reservation) => {
    try {
      const response = await reservationsApi.get(reservation.id);
      setViewingReservation(response.data);
    } catch (error) {
      toast.error('Failed to load reservation details');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      TENTATIVE: { variant: 'secondary', icon: Clock },
      CONFIRMED: { variant: 'default', icon: CheckCircle },
      CHECKED_IN: { variant: 'default', className: 'bg-green-500', icon: DoorOpen },
      CHECKED_OUT: { variant: 'secondary', icon: DoorClosed },
      CANCELLED: { variant: 'destructive', icon: XCircle },
      NO_SHOW: { variant: 'destructive', icon: X },
    };
    const config = variants[status] || { variant: 'secondary', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="h-3 w-3 mr-1" /> {status.replace('_', ' ')}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="reservations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Reservations</h1>
          <p className="text-muted-foreground">Manage bookings for {currentProperty?.name}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-reservation-btn">
              <Plus className="h-4 w-4 mr-2" /> New Reservation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>Create New Reservation</DialogTitle>
            </DialogHeader>
            <ReservationForm
              propertyId={currentProperty?.id}
              onSuccess={() => { setIsDialogOpen(false); fetchReservations(); }}
              onCancel={() => setIsDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <LogIn className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.arrivals}</p>
                  <p className="text-xs text-muted-foreground">Arrivals Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <LogOut className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.departures}</p>
                  <p className="text-xs text-muted-foreground">Departures Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Home className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.inhouse}</p>
                  <p className="text-xs text-muted-foreground">In House</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <DoorOpen className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_rooms}</p>
                  <p className="text-xs text-muted-foreground">Total Rooms</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.occupancy_rate}%</p>
                  <p className="text-xs text-muted-foreground">Occupancy</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All Reservations</TabsTrigger>
          <TabsTrigger value="arrivals" data-testid="tab-arrivals">Today&apos;s Arrivals</TabsTrigger>
          <TabsTrigger value="departures" data-testid="tab-departures">Today&apos;s Departures</TabsTrigger>
          <TabsTrigger value="inhouse" data-testid="tab-inhouse">In House</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                      <SelectItem value="TENTATIVE">Tentative</SelectItem>
                      <SelectItem value="CHECKED_IN">Checked In</SelectItem>
                      <SelectItem value="CHECKED_OUT">Checked Out</SelectItem>
                      <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Arrival From</Label>
                  <Input
                    type="date"
                    value={filters.arrival_from}
                    onChange={(e) => setFilters({ ...filters, arrival_from: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Arrival To</Label>
                  <Input
                    type="date"
                    value={filters.arrival_to}
                    onChange={(e) => setFilters({ ...filters, arrival_to: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Guest Name</Label>
                  <Input
                    placeholder="Search guest..."
                    value={filters.guest_name}
                    onChange={(e) => setFilters({ ...filters, guest_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirmation #</Label>
                  <Input
                    placeholder="RES..."
                    value={filters.confirmation}
                    onChange={(e) => setFilters({ ...filters, confirmation: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Table for all tabs */}
        <Card className="mt-4">
          <CardContent className="p-0">
            <Table data-testid="reservations-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Confirmation</TableHead>
                  <TableHead>Guest</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <TableCell key={j}><Skeleton className="h-4" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : reservations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground" data-testid="empty-reservations">
                      No reservations found.
                    </TableCell>
                  </TableRow>
                ) : (
                  reservations.map((res) => (
                    <TableRow key={res.id} data-testid={`reservation-row-${res.id}`}>
                      <TableCell className="font-mono text-sm">{res.confirmation_number}</TableCell>
                      <TableCell>
                        <div className="font-medium">{res.guest_name}</div>
                        {res.guest_email && <div className="text-xs text-muted-foreground">{res.guest_email}</div>}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{res.arrival_date}</div>
                        <div className="text-xs text-muted-foreground">to {res.departure_date}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{res.room_types?.name || res.room_type_id}</div>
                        {res.rooms?.room_number && (
                          <Badge variant="outline" className="text-xs">#{res.rooms.room_number}</Badge>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(res.status)}</TableCell>
                      <TableCell className="font-medium">ETB {res.grand_total?.toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => viewReservationDetails(res)} data-testid={`view-${res.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {res.status === 'CONFIRMED' && (
                            <Button variant="ghost" size="sm" onClick={() => handleCheckIn(res)} data-testid={`checkin-${res.id}`}>
                              <LogIn className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {res.status === 'CHECKED_IN' && (
                            <Button variant="ghost" size="sm" onClick={() => handleCheckOut(res)} data-testid={`checkout-${res.id}`}>
                              <LogOut className="h-4 w-4 text-amber-600" />
                            </Button>
                          )}
                          {['CONFIRMED', 'TENTATIVE'].includes(res.status) && (
                            <Button variant="ghost" size="sm" onClick={() => setCancelingReservation(res)} data-testid={`cancel-${res.id}`}>
                              <X className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Tabs>

      {/* View Reservation Dialog */}
      <Dialog open={!!viewingReservation} onOpenChange={() => setViewingReservation(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reservation Details</DialogTitle>
          </DialogHeader>
          {viewingReservation && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-lg">{viewingReservation.confirmation_number}</p>
                  {getStatusBadge(viewingReservation.status)}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">ETB {viewingReservation.grand_total?.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Guest Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{viewingReservation.guest_name}</span>
                    </div>
                    {viewingReservation.guest_email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{viewingReservation.guest_email}</span>
                      </div>
                    )}
                    {viewingReservation.guest_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{viewingReservation.guest_phone}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Stay Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-muted-foreground" />
                      <span>{viewingReservation.arrival_date} to {viewingReservation.departure_date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span>{viewingReservation.adults} adults, {viewingReservation.children} children</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-muted-foreground" />
                      <span>{viewingReservation.room_types?.name}</span>
                      {viewingReservation.rooms?.room_number && (
                        <Badge variant="outline">#{viewingReservation.rooms.room_number}</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {viewingReservation.special_requests && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Special Requests</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{viewingReservation.special_requests}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Reservation Dialog */}
      <Dialog open={!!cancelingReservation} onOpenChange={() => setCancelingReservation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Reservation</DialogTitle>
          </DialogHeader>
          <CancellationForm
            reservation={cancelingReservation}
            onSubmit={handleCancel}
            onCancel={() => setCancelingReservation(null)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReservationForm({ propertyId, onSuccess, onCancel }) {
  const { currentProperty } = useAuth();
  const isTC = currentProperty?.business_type === 'TRAINING_CENTER';
  const [step, setStep] = useState(1);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [tcBookings, setTcBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    guest: {
      guest_id: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
    },
    tc_booking_id: '',
    arrival_date: format(new Date(), 'yyyy-MM-dd'),
    departure_date: format(new Date(Date.now() + 86400000), 'yyyy-MM-dd'),
    adults: 2,
    children: 0,
    room_type_id: '',
    room_count: 1,
    rate_plan_id: '',
    rate_amount: '',
    special_requests: '',
    booking_channel: 'DIRECT',
    guarantee_type: 'NONE',
  });

  useEffect(() => {
    if (propertyId) {
      Promise.all([
        roomsApi.listTypes(propertyId),
        ratesApi.listPlans(propertyId)
      ]).then(([rtRes, rpRes]) => {
        setRoomTypes(rtRes.data);
        setRatePlans(rpRes.data);
      });
      if (isTC) {
        tcApi.listBookings(propertyId).then((res) => {
          setTcBookings((res.data || []).filter((b) => b.status !== 'CANCELLED'));
        }).catch(() => setTcBookings([]));
      }
    }
  }, [propertyId, isTC]);

  // Available rooms/beds for the selected dates & room type (for assignment)
  useEffect(() => {
    if (propertyId && formData.arrival_date && formData.departure_date) {
      reservationsApi.availableRooms(propertyId, {
        arrival_date: formData.arrival_date,
        departure_date: formData.departure_date,
        room_type_id: formData.room_type_id || undefined,
      }).then((r) => setAvailableRooms(r.data || [])).catch(() => setAvailableRooms([]));
    }
  }, [propertyId, formData.arrival_date, formData.departure_date, formData.room_type_id]);

  const selectTraining = (bookingId) => {
    const b = tcBookings.find((x) => x.id === bookingId);
    setFormData((f) => ({
      ...f,
      tc_booking_id: bookingId,
      ...(b ? {
        arrival_date: b.start_date || f.arrival_date,
        departure_date: b.end_date || f.departure_date,
        booking_channel: 'TRAINING',
        guest: { ...f.guest },
      } : {}),
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        ...formData,
        tc_booking_id: formData.tc_booking_id || null,
        guest: {
          ...formData.guest,
          guest_id: formData.guest.guest_id || null,
          email: formData.guest.email || null,
          phone: formData.guest.phone || null,
        },
        rate_amount: parseFloat(formData.rate_amount) || 0,
        special_requests: formData.special_requests || null,
      };
      await reservationsApi.create(propertyId, payload);
      toast.success('Reservation created successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reservation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reservation-form">
      {/* Step indicators */}
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              s === step ? 'bg-primary text-primary-foreground' : s < step ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'
            }`}
          >
            {s}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          {isTC && (
            <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
              <Label>Training Client / Program {!formData.tc_booking_id && <span className="text-amber-600">— recommended</span>}</Label>
              <Select value={formData.tc_booking_id || 'none'} onValueChange={(v) => selectTraining(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="res-training-client"><SelectValue placeholder="Link to a training booking" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not linked (walk-in / other) —</SelectItem>
                  {tcBookings.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.client_name}{b.company_name ? ` (${b.company_name})` : ''} · {b.start_date} → {b.end_date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Linking ties this trainee&apos;s room to the training client and fills the stay dates from the program.</p>
            </div>
          )}
          <h3 className="font-medium">Guest Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First Name *</Label>
              <Input
                value={formData.guest.first_name}
                onChange={(e) => setFormData({ ...formData, guest: { ...formData.guest, first_name: e.target.value } })}
                required
                data-testid="res-guest-firstname"
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name *</Label>
              <Input
                value={formData.guest.last_name}
                onChange={(e) => setFormData({ ...formData, guest: { ...formData.guest, last_name: e.target.value } })}
                required
                data-testid="res-guest-lastname"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.guest.email}
                onChange={(e) => setFormData({ ...formData, guest: { ...formData.guest, email: e.target.value } })}
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.guest.phone}
                onChange={(e) => setFormData({ ...formData, guest: { ...formData.guest, phone: e.target.value } })}
              />
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="font-medium">Stay Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Check-in Date *</Label>
              <Input
                type="date"
                value={formData.arrival_date}
                onChange={(e) => setFormData({ ...formData, arrival_date: e.target.value })}
                data-testid="res-checkin"
              />
            </div>
            <div className="space-y-2">
              <Label>Check-out Date *</Label>
              <Input
                type="date"
                value={formData.departure_date}
                onChange={(e) => setFormData({ ...formData, departure_date: e.target.value })}
                data-testid="res-checkout"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Adults</Label>
              <Select value={String(formData.adults)} onValueChange={(v) => setFormData({ ...formData, adults: parseInt(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Children</Label>
              <Select value={String(formData.children)} onValueChange={(v) => setFormData({ ...formData, children: parseInt(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 1, 2, 3, 4].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Rooms</Label>
              <Select value={String(formData.room_count)} onValueChange={(v) => setFormData({ ...formData, room_count: parseInt(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Room Type *</Label>
            <Select value={formData.room_type_id} onValueChange={(v) => setFormData({ ...formData, room_type_id: v })}>
              <SelectTrigger data-testid="res-room-type"><SelectValue placeholder="Select room type" /></SelectTrigger>
              <SelectContent>
                {roomTypes.map((rt) => (
                  <SelectItem key={rt.id} value={rt.id}>{rt.name} ({rt.code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Assign a specific room / bed from live availability (links Lodging ↔ Facilities) */}
          <div className="space-y-2">
            <Label>Assign Room / Bed {formData.room_id && <span className="text-emerald-600 text-xs">— assigned</span>}</Label>
            {(!formData.arrival_date || !formData.departure_date) ? (
              <p className="text-xs text-muted-foreground">Choose check-in/out dates to see available rooms.</p>
            ) : availableRooms.length === 0 ? (
              <p className="text-xs text-muted-foreground">No rooms found for these dates.</p>
            ) : (
              <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto p-1 border rounded-lg">
                {availableRooms.map((rm) => (
                  <button
                    key={rm.id}
                    type="button"
                    disabled={!rm.available}
                    onClick={() => setFormData({ ...formData, room_id: formData.room_id === rm.id ? null : rm.id })}
                    title={rm.room_type || ''}
                    className={[
                      'px-3 py-2 rounded-md border text-sm transition',
                      !rm.available ? 'opacity-50 cursor-not-allowed bg-red-50 border-red-200 text-red-500'
                        : formData.room_id === rm.id ? 'bg-primary text-primary-foreground border-primary'
                        : 'hover:border-primary border-slate-200',
                    ].join(' ')}
                    data-testid={`assign-room-${rm.room_number}`}
                  >
                    {rm.room_number}{rm.floor ? ` · Fl ${rm.floor}` : ''}
                    {!rm.available && <span className="block text-[10px]">occupied</span>}
                  </button>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">Green = selected · faded/red = occupied for these dates. Optional — leave unassigned to assign at check-in.</p>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="font-medium">Rate & Payment</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rate Plan *</Label>
              <Select value={formData.rate_plan_id} onValueChange={(v) => {
                const plan = ratePlans.find(p => p.id === v);
                setFormData({ ...formData, rate_plan_id: v, rate_amount: plan?.base_rate || '' });
              }}>
                <SelectTrigger data-testid="res-rate-plan"><SelectValue placeholder="Select rate plan" /></SelectTrigger>
                <SelectContent>
                  {ratePlans.map((rp) => (
                    <SelectItem key={rp.id} value={rp.id}>{rp.name} - ETB {rp.base_rate}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nightly Rate</Label>
              <Input
                type="number"
                value={formData.rate_amount}
                onChange={(e) => setFormData({ ...formData, rate_amount: e.target.value })}
                data-testid="res-rate"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Booking Channel</Label>
              <Select value={formData.booking_channel} onValueChange={(v) => setFormData({ ...formData, booking_channel: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DIRECT">Direct</SelectItem>
                  <SelectItem value="WEBSITE">Website</SelectItem>
                  <SelectItem value="OTA">OTA</SelectItem>
                  <SelectItem value="CORPORATE">Corporate</SelectItem>
                  <SelectItem value="TRAVEL_AGENT">Travel Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Guarantee</Label>
              <Select value={formData.guarantee_type} onValueChange={(v) => setFormData({ ...formData, guarantee_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">None</SelectItem>
                  <SelectItem value="CREDIT_CARD">Credit Card</SelectItem>
                  <SelectItem value="DEPOSIT">Deposit</SelectItem>
                  <SelectItem value="PREPAID">Prepaid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Special Requests</Label>
            <Textarea
              value={formData.special_requests}
              onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
              placeholder="Any special requests or notes..."
            />
          </div>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={step > 1 ? () => setStep(s => s - 1) : onCancel}>
          {step > 1 ? 'Back' : 'Cancel'}
        </Button>
        {step < 3 ? (
          <Button onClick={() => setStep(s => s + 1)} data-testid="res-next-btn">Next</Button>
        ) : (
          <Button onClick={handleSubmit} disabled={loading} data-testid="res-submit-btn">
            {loading ? 'Creating...' : 'Create Reservation'}
          </Button>
        )}
      </div>
    </div>
  );
}

function CancellationForm({ reservation, onSubmit, onCancel }) {
  const [reason, setReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(reason);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-muted-foreground">
        Are you sure you want to cancel reservation <span className="font-mono font-medium">{reservation?.confirmation_number}</span>?
      </p>
      <div className="space-y-2">
        <Label>Cancellation Reason *</Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter reason for cancellation..."
          required
          data-testid="cancel-reason"
        />
      </div>
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>Keep Reservation</Button>
        <Button type="submit" variant="destructive" data-testid="confirm-cancel-btn">Cancel Reservation</Button>
      </div>
    </form>
  );
}
